import { Alert, AlertTitle, Box, Chip, Grid, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useRecordContext } from 'react-admin';
import FeatureIntro from '../../../components/FeatureIntro';
import {
  AppArt, AppArtStatus, STATUS_COLOR, STATUS_GUIDE, STATUS_LABEL, STATUS_STEPS, STEP_INDEX, STEP_LABEL,
} from '../types';
import DownloadAllButton from './DownloadAllButton';
import { adjustableModels } from '../lib/artboards';
import AdjustCommandButton from './AdjustCommandButton';
import DownloadAssetsButton from './DownloadAssetsButton';
import GenerateCommandButton from './GenerateCommandButton';
import ViewInAppButton from './ViewInAppButton';

/** Onde a arte está no fluxo e qual é o próximo passo. */
const ArtStatusHeader = () => {
  const record = useRecordContext<AppArt>();
  if (!record) { return null; }

  const guide = STATUS_GUIDE[record.status];
  const stepIndex = STEP_INDEX[record.status] ?? 0;
  // Rodada nova pedida não é passo do fluxo: é o passo "Modelos" em estado de
  // pendência. O stepper marca com erro em vez de fingir que voltou ao começo.
  const detour = record.status === AppArtStatus.changesRequested;
  // A geração é a etapa que roda fora do admin — o comando fica à mão nos dois
  // status em que o gerador é quem destrava a rede.
  const waitingGenerator = [AppArtStatus.submitted, AppArtStatus.changesRequested]
    .includes(record.status);
  // Rede com arte feita fora daqui não tem passo nenhum para mostrar — desenhar
  // o stepper acendendo "Material" diria que ela está no começo de um fluxo em
  // que ela nunca entrou.
  const outOfFlow = record.status === AppArtStatus.legacy;
  // O ajuste é sobre uma arte que existe. A condição vive aqui também porque o
  // FeatureIntro precisa saber se há botão: ancorado num filho que não
  // renderiza, o popover aparece apontando para o nada.
  const canAdjust = adjustableModels(record.files || []).length > 0;
  // Os assets do app só fazem sentido depois que a arte está fechada.
  const artClosed = [AppArtStatus.modelChosen, AppArtStatus.published]
    .includes(record.status);

  return (
    <Grid size={12}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={STATUS_LABEL[record.status]}
            color={STATUS_COLOR[record.status]}
          />
          <Box sx={{ flexGrow: 1 }} />
          {!outOfFlow && (
            <FeatureIntro
              id="app-art-generate-command"
              title="Gerar os modelos desta rede"
              description="O gerador roda na sua máquina, não no servidor. Aqui você monta o comando já com o id da rede e as opções, e cola no terminal."
            >
              <GenerateCommandButton highlighted={waitingGenerator} />
            </FeatureIntro>
          )}
          {!outOfFlow && canAdjust && (
            <FeatureIntro
              id="app-art-adjust-command"
              title="O cliente quer aquela arte, só que diferente"
              description="Em vez de gerar tudo de novo, o gerador pega o desenho do modelo escolhido, olha a arte que o cliente viu e aplica só a mudança pedida. Aqui você escreve o pedido e leva o comando pronto."
            >
              <AdjustCommandButton />
            </FeatureIntro>
          )}
          {!outOfFlow && <ViewInAppButton editable={record.status === AppArtStatus.draft} />}
          {artClosed && <DownloadAssetsButton />}
          <DownloadAllButton files={record.files || []} label="Baixar material" />
        </Box>

        {!outOfFlow && (
          <Stepper activeStep={stepIndex} sx={{ mb: 2 }}>
            {STATUS_STEPS.map((step, index) => (
              <Step key={step} completed={index < stepIndex}>
                <StepLabel error={detour && index === stepIndex}>
                  {STEP_LABEL[step]}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {guide && (
          <Alert severity={guide.severity} icon={false}>
            <AlertTitle sx={{ mb: 0.5 }}>{guide.what}</AlertTitle>
            <Typography variant="body2">{guide.next}</Typography>
          </Alert>
        )}
      </Paper>
    </Grid>
  );
};

export default ArtStatusHeader;
